/*
 *  @providesModule LTOModal
 *
 */
import DepositOfferTile from 'DepositOfferTile';
import GradientLabel from 'GradientLabel';
import HeaderModal from 'HeaderModal';
import {_m} from 'INTL';
import I18n from 'INTL';
import InsufficientFunds from 'InsufficientFunds';
import styles from 'LTOModalStyle';
import Label from 'Label';
import ModalNavigationController from 'ModalNavigationController';
import NavigationManager from 'NavigationManager';
import Optional from 'Optional';
import PushButton from 'PushButton';
import SKZConstants from 'SKZConstants';
import SkillzStyle from 'SkillzStyle';
import {LTOInfo} from 'StoreInfo';
import _ from 'lodash';
import moment from 'moment';
import React from 'react';
import {View} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';

const {btnGradientOne, btnGradientStore, btnGradientTwo, viewCornerRadiusSecondary} =
  SkillzStyle.fullStyles;

const HISTORICAL = 'HISTORICAL';
const NEW = 'NEW';
const BONUS_CASH = _m('BONUS CASH');

type Props = {
  modalStyle: 'HISTORICAL' | 'NEW';
  title?: string;
  okButtonTitle?: string;
  cancelButtonTitle?: string;
  bodyText?: string;
  onDone?: () => void;
  onCancel?: () => void;
  offerInfo: LTOInfo;
};

type State = {
  endsInText: string;
};

export default class LTOModal extends React.Component<Props, State> {
  offerCountdownTimer?: NodeJS.Timeout | null;

  static defaultProps = {
    title: _m('ADD FUNDS'),
    okButtonTitle: _m('NEXT'),
    cancelButtonTitle: _m('BACK'),
    bodyText: _m('LIMITED TIME OFFER!'),
    hideCancelButton: false,
    onCancel: () => {},
    onDone: () => {},
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      endsInText: '',
    };
  }

  componentDidMount() {
    this._startCountdownTimer();
  }

  componentWillUnmount() {
    this._cancelTimer();
  }

  _hideModal() {
    ModalNavigationController.hideModal();
  }

  _renderLTOInfo(): React.ReactElement {
    return (
      <View style={styles.offerContainer}>
        {this._renderDepositTile(true)}
        <View style={styles.plusContainer}>
          <GradientLabel style={styles.gradientText}>+</GradientLabel>
        </View>
        {this._renderDepositTile(false)}
      </View>
    );
  }

  _renderDepositTile(isDeposit: boolean): React.ReactElement {
    const amount = isDeposit ? this.props.offerInfo?.amount : this.props.offerInfo.bonus;
    const cashAmount = I18n.getCurrencyString(amount, '$', true, true);
    const depositTileProps = {primaryText: cashAmount, onChange: this.props.onDone};
    const subText = isDeposit ? '' : BONUS_CASH;
    return <DepositOfferTile {...depositTileProps} subText={subText} />;
  }

  _renderLTOCountdown(): React.ReactElement {
    return (
      <View style={styles.countdownContainer}>
        <Label style={styles.countdownTimeText} key={this.state.endsInText} accessible={false}>
          {' ' + this.state.endsInText}
        </Label>
      </View>
    );
  }

  _fireFirst() {
    if (this.props.offerInfo && this.props.offerInfo.end) {
      const endDate = new Date(this.props.offerInfo.end);
      if (moment() < endDate) {
        this.setState({
          endsInText: I18n.getSimplifiedTime(this.props.offerInfo.end),
        });
      }
    } else {
      this._cancelTimer();
    }
  }

  _startCountdownTimer() {
    this._fireFirst();
    this._cancelTimer();
    this.offerCountdownTimer = setInterval(() => {
      this._fireFirst();
    }, SKZConstants.MS_PER_SEC);
  }

  _cancelTimer() {
    if (this.offerCountdownTimer) {
      clearInterval(this.offerCountdownTimer);
      this.offerCountdownTimer = null;
    }
  }

  _renderHistoricalModal(): React.ReactElement {
    const navigator = NavigationManager.getNavigator();
    return (
      <InsufficientFunds
        mainNavigator={navigator}
        isCash={true}
        onDone={() => {
          navigator.pop();
          ModalNavigationController.hideModal();
        }}
      />
    );
  }

  _renderLTOSection(isNew: boolean): React.ReactElement {
    const colors = isNew ? [btnGradientStore[0], btnGradientStore[1]] : ['#FFFFFF'];
    return (
      <View style={styles.modalContainer}>
        <View style={styles.borderContainer}>
          <LinearGradient style={styles.gradient} colors={colors}>
            <View style={styles.contentContainer}>
              <Label numberOfLines={0} style={[styles.bodyMargins, styles.bodyText]}>
                {this.props.bodyText}
              </Label>
              {this._renderLTOInfo()}
              {this._renderLTOCountdown()}
            </View>
          </LinearGradient>
        </View>
      </View>
    );
  }

  _renderButtons(isNew: boolean): React.ReactElement {
    return (
      <View style={styles.modalContainer}>
        <View style={styles.buttonContainer}>
          <Optional test={isNew}>
            <PushButton
              onPress={this.props.onCancel}
              style={styles.buttonLayout}
              textStyle={styles.pushButtonText}
              colors={btnGradientTwo}
              title={this.props.cancelButtonTitle}
              cornerRadius={viewCornerRadiusSecondary.borderRadius}
              accessibilityLabel={'lto_modal_cancel'}
            />
          </Optional>
          <PushButton
            onPress={this.props.onDone}
            style={styles.buttonLayout}
            textStyle={styles.pushButtonText}
            colors={btnGradientOne}
            title={this.props.okButtonTitle}
            cornerRadius={viewCornerRadiusSecondary.borderRadius}
            accessibilityLabel={'lto_modal_next'}
          />
        </View>
      </View>
    );
  }

  render(): React.ReactElement {
    if (this.props.modalStyle === HISTORICAL) {
      return this._renderHistoricalModal();
    } else {
      const isNew = this.props.modalStyle === NEW;
      return (
        <HeaderModal
          title={this.props.title}
          isAnimated={true}
          animationDuration={750}
          animationType={'zoomIn'}
          useNativeDriver={true}
          easing={'ease-in-out-back'}
        >
          {this._renderLTOSection(isNew)}
          {this._renderButtons(isNew)}
        </HeaderModal>
      );
    }
  }
}
